import { AlgoliaFacets } from '@utils/algolia/knownFacetNames';
import { isEffectivePriceAttribute } from '@utils/algolia/priceContext';

/**
 * Per-attribute value ordering from the index's `renderingContent`. `hide`
 * lists values the engine must not surface, and `sortRemainingBy: 'hidden'`
 * suppresses every value missing from `order`.
 */
type FacetValueOrdering = {
  order?: string[];
  hide?: string[];
  sortRemainingBy?: 'count' | 'alpha' | 'hidden';
};

type RenderingContent = {
  facetOrdering?: { values?: Record<string, FacetValueOrdering> };
};

type RawResult = {
  facets?: Record<string, Record<string, number>>;
  renderingContent?: RenderingContent;
};

type SearchResultsLike = {
  _rawResults?: RawResult[];
  disjunctiveFacets?: { name: string }[];
  facets_stats?: Record<string, { sum: number }>;
  renderingContent?: RenderingContent;
};

/**
 * Shapes the ordered facet list surfaced by the Algolia index's
 * `renderingContent.facetOrdering` (via DynamicWidgets / useDynamicWidgets)
 * before it is rendered:
 * - hides the size-bucket facet unless the results expose gendered shoe sizes,
 *   and always on gendered/clothing PLPs where clothing sizes take over
 * - drops the rating facet when no product in the result set has a review
 * - swaps whichever price facet the index lists for the active pricing
 *   context's attribute, deduping if ordering lists more than one
 */
export const makeFacetTransformItems =
  ({
    categoryUrl,
    priceAttribute,
  }: {
    categoryUrl: string;
    priceAttribute: string;
  }) =>
  (items: string[], { results }: { results?: SearchResultsLike } = {}) => {
    let showShoeBuckets = false;

    let filtered = items;

    const isShoeSizeFacet = (name: string) => name.split('size.').length > 1;
    for (const raw of results?._rawResults ?? []) {
      for (const name of Object.keys(raw.facets ?? {})) {
        if (isShoeSizeFacet(name)) {
          showShoeBuckets = true;
        }
      }
    }

    for (const facet of results?.disjunctiveFacets ?? []) {
      if (isShoeSizeFacet(facet.name)) {
        showShoeBuckets = true;
      }
    }

    const suppressedShoeSizeUrlFragments = [
      '/products/womens',
      '/products/mens',
      '/products/clothing',
      '/produits/femmes',
      '/produits/hommes',
      '/produits/vetements',
    ];

    for (const fragment of suppressedShoeSizeUrlFragments) {
      if (categoryUrl.includes(fragment)) {
        showShoeBuckets = false;
      }
    }

    if (!showShoeBuckets) {
      filtered = filtered.filter((i) => i !== 'size');
    }
    filtered = filtered.filter(
      (i) => i !== AlgoliaFacets.ShoeWidth && !i.startsWith('size.')
    );

    if (results?.facets_stats?.reviewScoreFlattened?.sum === 0) {
      filtered = filtered.filter(
        (i) => i !== AlgoliaFacets.ReviewScoreFlattened
      );
    }

    const seen = new Set<string>();
    return filtered
      .map((i) =>
        i === AlgoliaFacets.Price || isEffectivePriceAttribute(i)
          ? priceAttribute
          : i
      )
      .filter((i) => {
        if (seen.has(i)) return false;
        seen.add(i);
        return true;
      });
  };

const getFacetValueOrdering = (
  results: SearchResultsLike | undefined,
  name: string
): FacetValueOrdering | undefined =>
  (results?.renderingContent ?? results?._rawResults?.[0]?.renderingContent)
    ?.facetOrdering?.values?.[name];

/**
 * Whether a facet has at least one value a refinement widget would actually
 * render. Raw facet values are not enough: `getFacetValues` (algoliasearch-
 * helper) drops values the index's `renderingContent` marks as hidden, so a
 * facet whose every returned value is hidden reaches the UI with an empty
 * list — an empty pill dropdown / all-filters section (ED-320).
 */
const facetHasValues = (
  results: SearchResultsLike | undefined,
  name: string
): boolean => {
  const ordering = getFacetValueOrdering(results, name);
  const hidden = new Set(ordering?.hide ?? []);
  const visibleByOrder =
    ordering?.sortRemainingBy === 'hidden'
      ? new Set(ordering.order ?? [])
      : null;

  return (results?._rawResults ?? []).some((raw) =>
    Object.keys(raw.facets?.[name] ?? {}).some(
      (value) =>
        !hidden.has(value) && (!visibleByOrder || visibleByOrder.has(value))
    )
  );
};

/**
 * Per-pass memo over `facetHasValues`: `results` is fixed for the duration of a
 * filter pass, so each attribute's verdict — and the ordering sets built to
 * reach it — is computed at most once even when several aliases resolve to the
 * same underlying attribute (e.g. colour/colourVariants, rating).
 */
const makeFacetValueCheck = (results: SearchResultsLike | undefined) => {
  const cache = new Map<string, boolean>();

  return (name: string): boolean => {
    const cached = cache.get(name);
    if (cached !== undefined) return cached;

    const result = facetHasValues(results, name);
    cache.set(name, result);
    return result;
  };
};

/**
 * The `size.<bucket>` facet names present in the raw results. The index has no
 * plain `size` attribute — the size-bucket widget reads `size.US Men's`,
 * `size.EU`, … and the synthetic `size` entry in the index's `facetOrdering`
 * stands in for all of them.
 */
const shoeSizeFacetNames = (
  results: SearchResultsLike | undefined
): string[] => {
  const names = new Set<string>();
  for (const raw of results?._rawResults ?? []) {
    for (const name of Object.keys(raw.facets ?? {})) {
      if (name.startsWith('size.')) names.add(name);
    }
  }
  return [...names];
};

export const filterFacetsWithValues = (
  facets: string[],
  {
    results,
    priceAttribute,
    hasRefinement,
  }: {
    results?: SearchResultsLike;
    priceAttribute: string;
    hasRefinement?: (attribute: string) => boolean;
  }
): string[] => {
  if (!results) return facets;

  const hasValues = makeFacetValueCheck(results);

  return facets.filter((attribute) => {
    if (attribute.startsWith('categories')) return true;
    if (hasRefinement?.(attribute)) return true;
    if (
      attribute === priceAttribute ||
      attribute === AlgoliaFacets.Price ||
      isEffectivePriceAttribute(attribute)
    ) {
      return true;
    }
    // Checking `size` itself is always false (no such attribute in the index),
    // which hid the shoe-size facet on every PLP under FEATURE_FLAG_PLP_FILTERS_V2.
    // Its values live under the `size.<bucket>` keys instead.
    if (attribute === 'size') {
      return shoeSizeFacetNames(results).some(hasValues);
    }
    // `rating` is a UI-only alias: FacetRefinement always mounts the rating
    // widget on `reviewScoreFlattened` (see normalizeFacetKey), so that is the
    // only attribute whose values decide whether the facet renders anything.
    if (
      attribute === AlgoliaFacets.Rating ||
      attribute === AlgoliaFacets.ReviewScoreFlattened
    ) {
      return hasValues(AlgoliaFacets.ReviewScoreFlattened);
    }
    if (
      attribute === AlgoliaFacets.Colour ||
      attribute === AlgoliaFacets.ColourVariants
    ) {
      return (
        hasValues(AlgoliaFacets.ColourVariants) ||
        hasValues(AlgoliaFacets.Colour) ||
        Boolean(hasRefinement?.(AlgoliaFacets.ColourVariants)) ||
        Boolean(hasRefinement?.(AlgoliaFacets.Colour))
      );
    }
    return hasValues(attribute);
  });
};
